/**
 * Worksheet API client — Phase 4.3.
 *
 * Narrow client over:
 *   - GET  /v1/worksheet/:id?child_id=N     full detail + temaNames[] + approved
 *   - GET  /v1/worksheet/:id/pdf            PDF binary via proxy (hindari CORS CDN)
 *   - POST /v1/worksheet/:id/print-status?child_id=N  update print_status
 *   - POST /v1/worksheet/:id/complete?child_id=N      mark as done
 *
 * Same pattern as daily.ts / children.ts / history.ts — thin wrapper
 * around apiFetch with type-safe response handling.
 */

import { API_BASE_URL, ApiError, apiFetch } from './client';
import { clearAuthSession, getAuthToken } from '../auth';
import { getOrCreateDeviceId } from '../device-id';
import type { HistoryStatus } from '@/types/history';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/** Full worksheet detail (mirrors server response from /v1/worksheet/:id). */
export interface WorksheetDetail {
  id: number;
  source_table: string;
  source_id: number;
  source_kind: string | null;
  name: string;
  headline: string | null;
  instruction: string | null;
  theme_id: number | null;
  theme_label: string | null;
  activity_id: number | null;
  activity_label: string | null;
  age_id: number | null;
  age_label: string | null;
  age_range: string | null;
  class_id: number | null;
  class_label: string | null;
  category_id: number | null;
  category_label: string | null;
  theme_calistung_id: number | null;
  theme_calistung_label: string | null;
  difficulty: string | null;
  duration_minutes: number | null;
  pdf_url: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  downloaded: number;
  interactive: number;
  played: number;
  is_active: number;
  funpaper_type_id: number | null;
  created_at: string;
  updated_at: string;
  /** 14 PAUD-specific tema names (sorted by sort_order, may be empty). */
  temaNames: string[];
}

/** Approval row attached to the detail response (if user has approved this ws). */
export interface WorksheetApproved {
  id: number;
  childProfileId: number;
  approvedAt: string;
  completedAt: string | null;
  printStatus: string;
  completionFeedback: string | null;
}

/** Print status enum (matches the API contract). */
export type PrintStatus = 'printed' | 'shared_pdf' | 'shared_wa';

// ----------------------------------------------------------------------------
// GET /v1/worksheet/:id?child_id=N
// ----------------------------------------------------------------------------

type GetWorksheetDetailResponse = {
  ok: true;
  worksheet: WorksheetDetail;
  approved: WorksheetApproved | null;
};

/**
 * Fetch full worksheet detail + the user's approval status (if any).
 *
 * @param worksheetId  Worksheet id (from history card or home card)
 * @param childId      Optional child id — used to scope the approved row
 *                     lookup. When null, returns the most recent approved
 *                     row across all children of this device.
 */
export async function getWorksheetDetail(
  worksheetId: number,
  childId?: number | null,
): Promise<{ worksheet: WorksheetDetail; approved: WorksheetApproved | null }> {
  const query = childId ? `?child_id=${childId}` : '';
  const response = await apiFetch<GetWorksheetDetailResponse>(
    `/v1/worksheet/${worksheetId}${query}`,
  );
  return {
    worksheet: response.worksheet,
    approved: response.approved,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/worksheet/:id/print-status?child_id=N
// ----------------------------------------------------------------------------

type PostPrintStatusResponse = {
  ok: true;
  approvedId: number;
  printStatus: PrintStatus;
};

/**
 * Update the print status of an approved worksheet.
 * Idempotent — safe to call multiple times.
 */
export async function updatePrintStatus(
  worksheetId: number,
  childId: number,
  printStatus: PrintStatus,
): Promise<{ approvedId: number; printStatus: PrintStatus }> {
  const response = await apiFetch<PostPrintStatusResponse>(
    `/v1/worksheet/${worksheetId}/print-status?child_id=${childId}`,
    { method: 'POST', body: { printStatus } },
  );
  return {
    approvedId: response.approvedId,
    printStatus: response.printStatus,
  };
}

// ----------------------------------------------------------------------------
// POST /v1/worksheet/:id/complete?child_id=N
// ----------------------------------------------------------------------------

type PostCompleteResponse = {
  ok: true;
  approvedId: number;
  completedAt: string;
};

/**
 * Mark an approved worksheet as done by the child.
 * Sets `approved_worksheets.completed_at` to the current timestamp.
 */
export async function markComplete(
  worksheetId: number,
  childId: number,
  completionFeedback?: string,
): Promise<{ approvedId: number; completedAt: string }> {
  const response = await apiFetch<PostCompleteResponse>(
    `/v1/worksheet/${worksheetId}/complete?child_id=${childId}`,
    {
      method: 'POST',
      body: completionFeedback ? { completionFeedback } : {},
    },
  );
  return {
    approvedId: response.approvedId,
    completedAt: response.completedAt,
  };
}

// ----------------------------------------------------------------------------
// GET /v1/worksheet/:id/pdf  (binary, via proxy server)
// ----------------------------------------------------------------------------

/**
 * Fetch worksheet PDF sebagai Blob lewat proxy API (server meneruskan dari
 * CDN). Tidak pakai apiFetch karena response-nya binary, bukan JSON.
 *
 * Dipakai "Simpan PDF" di web — fetch langsung ke CDN selalu gagal karena
 * asset.terusmengajar.id tidak mengirim header CORS (2026-08-19).
 */
export async function fetchWorksheetPdfBlob(worksheetId: number): Promise<Blob> {
  const token = getAuthToken();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/v1/worksheet/${worksheetId}/pdf`, {
      headers: {
        'X-Device-Id': getOrCreateDeviceId(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (err) {
    throw new ApiError(
      `Network error: ${err instanceof Error ? err.message : 'unable to reach server'}`,
      0,
      'NetworkError',
    );
  }
  if (!response.ok) {
    // Sesi yang ditolak server tidak akan valid lagi — buang dari storage.
    if (response.status === 401) clearAuthSession();
    const body = (await response.json().catch(() => undefined)) as
      | { message?: unknown }
      | undefined;
    throw new ApiError(
      typeof body?.message === 'string' ? body.message : 'Gagal mengunduh PDF.',
      response.status,
    );
  }
  return response.blob();
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/** Map server's print_status string to the HistoryStatus enum used by the UI. */
export function toHistoryStatus(
  printStatus: string,
  completedAt: string | null,
): HistoryStatus {
  if (printStatus === 'printed') return 'printed';
  if (printStatus === 'shared_pdf') return 'shared_pdf';
  if (printStatus === 'shared_wa') return 'shared_wa';
  if (completedAt) return 'selesai_belum_dicetak';
  return 'belum_dicetak';
}
